import React, { useState, useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import GenericModal from "components/shared/GenericModal";
import { PLANS, PLANS_META } from "@routebot/constants";

/**
 * Custom hook for checking route limits before creating new routes
 * Provides a modal component, check functions, and limit status
 */
export const useRouteLimit = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [limitStatus, setLimitStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { sendRequest } = useHttpClient();
  const { t } = useTranslation();
  const auth = useContext(AuthContext);

  const isDemo = auth?.company?.isDemo;
  const isUnlimitedPlan = auth?.company?.plan === PLANS.UNLIMITED;

  const isRouteLimitUnlimited = (status) => Boolean(status?.unlimited || isUnlimitedPlan);

  /**
   * Fetch current route limit status from the server
   */
  const fetchLimitStatus = useCallback(async () => {
    if (isUnlimitedPlan) {
      setLimitStatus({
        current: 0,
        max: null,
        remaining: null,
        percentage: 0,
        unlimited: true,
        allowed: true,
      });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/route-limit/status`, "GET");
      const unlimited = isRouteLimitUnlimited(responseData);
      setLimitStatus({
        ...responseData,
        unlimited,
        allowed: unlimited || responseData.allowed !== false,
      });
    } catch {
      // Fail-open: if the check fails, assume allowed
      const plan = auth?.company?.plan;
      const maxRoutes = PLANS_META[plan]?.maxRoutes || 0;
      setLimitStatus({
        current: 0,
        max: maxRoutes || null,
        remaining: maxRoutes || null,
        unlimited: !maxRoutes,
        percentage: 0,
        allowed: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [sendRequest, isUnlimitedPlan, auth?.company?.plan]);

  // Fetch status on mount
  useEffect(() => {
    fetchLimitStatus();
  }, [fetchLimitStatus]);

  /**
   * Shows demo account limit modal
   */
  const showDemoLimitModal = useCallback(() => {
    setModalTitle(t("routeLimitExceededForDemo"));
    setModalMessage(t("routeLimitExceededForDemoDetailed"));
    setShowModal(true);
  }, [t]);

  /**
   * Check if user can create a new route
   * Shows a modal with appropriate message if limit is reached
   * @param {number} additionalRoutes - Number of routes to add (default: 1)
   * @returns {Promise<{allowed: boolean, current: number, max: number, remaining: number}>}
   */
  const checkRouteLimit = useCallback(
    async (additionalRoutes = 1) => {
      if (isUnlimitedPlan) {
        return {
          allowed: true,
          unlimited: true,
          current: 0,
          max: null,
          remaining: null,
        };
      }

      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/route-limit/check`,
          "POST",
          JSON.stringify({ additionalRoutes }),
        );

        if (!responseData.allowed) {
          if (isDemo) {
            showDemoLimitModal();
          } else {
            setModalTitle(t("routeLimitExceeded"));
            setModalMessage(
              t("routeLimitExceededDetailed", {
                current: responseData.current,
                max: responseData.max,
              }),
            );
            setShowModal(true);
          }
        }

        return responseData;
      } catch {
        // Fail-open: if the check fails, allow the operation
        return {
          allowed: true,
          unlimited: true,
          current: 0,
          max: null,
          remaining: null,
        };
      }
    },
    [sendRequest, t, isDemo, showDemoLimitModal, isUnlimitedPlan],
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  /**
   * Check if the route limit is reached (synchronous check based on cached status)
   * UNLIMITED users can never have their limit reached
   */
  const isLimitReached =
    isUnlimitedPlan || limitStatus?.unlimited
      ? false
      : limitStatus
        ? limitStatus.allowed === false || (limitStatus.remaining !== null && limitStatus.remaining <= 0)
        : false;

  /**
   * Modal component to be rendered in the parent component
   */
  const RouteLimitModal = useCallback(
    () => <GenericModal message={modalMessage} title={modalTitle} onClose={closeModal} showModal={showModal} />,
    [modalMessage, modalTitle, showModal, closeModal],
  );

  /**
   * Warning banner component to show when approaching or at limit
   */
  const RouteLimitWarning = useCallback(() => {
    if (isLoading || !limitStatus || isUnlimitedPlan || limitStatus.unlimited) {
      return null;
    }

    const { current, max, remaining, percentage } = limitStatus;

    // Show warning when at 80% or more usage, or when limit is reached
    if (percentage < 80 && remaining > 0) {
      return null;
    }

    const isAtLimit = remaining <= 0;
    const warningStyle = {
      padding: "12px 16px",
      marginBottom: "16px",
      borderRadius: "8px",
      backgroundColor: isAtLimit ? "#fef2f2" : "#fffbeb",
      border: `1px solid ${isAtLimit ? "#fecaca" : "#fde68a"}`,
      color: isAtLimit ? "#991b1b" : "#92400e",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    };

    const iconStyle = {
      fontSize: "20px",
      flexShrink: 0,
    };

    const textStyle = {
      flex: 1,
    };

    const titleStyle = {
      fontWeight: 600,
      marginBottom: "4px",
    };

    const descriptionStyle = {
      fontSize: "14px",
      opacity: 0.9,
    };

    return (
      <div style={warningStyle}>
        <span style={iconStyle}>{isAtLimit ? "⚠️" : "📊"}</span>
        <div style={textStyle}>
          <div style={titleStyle}>{isAtLimit ? t("routeLimitReached") : t("routeLimitApproaching")}</div>
          <div style={descriptionStyle}>{t("routeLimitStatus", { current, max, remaining })}</div>
        </div>
      </div>
    );
  }, [isLoading, limitStatus, isUnlimitedPlan, t]);

  return {
    checkRouteLimit,
    fetchLimitStatus,
    RouteLimitModal,
    RouteLimitWarning,
    closeModal,
    isModalOpen: showModal,
    limitStatus,
    isLoading,
    isLimitReached,
    isUnlimited: isUnlimitedPlan || Boolean(limitStatus?.unlimited),
  };
};

export default useRouteLimit;
